import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Container, Card, Row, Col, Form, Button, Alert, Modal } from 'react-bootstrap';
import { Formik, Field, ErrorMessage as FormikErrorMessage } from 'formik';
import * as Yup from 'yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSave, faInfoCircle, faFilePdf, faDownload } from '@fortawesome/free-solid-svg-icons';
import { guaranteeService } from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorMessage from '../common/ErrorMessage';
import DocumentPreview from '../common/DocumentPreview';
import GuaranteePrice from './GuaranteePrice';

// Validation schema using Yup
const validationSchema = Yup.object().shape({
  customer: Yup.object().shape({
    firstName: Yup.string().required('Vorname ist erforderlich'),
    lastName: Yup.string().required('Nachname ist erforderlich'),
    street: Yup.string().required('Straße ist erforderlich'),
    postalCode: Yup.string().required('Postleitzahl ist erforderlich'),
    city: Yup.string().required('Stadt ist erforderlich'),
  }),
  vehicle: Yup.object().shape({
    licensePlate: Yup.string().required('Kennzeichen ist erforderlich'),
    duration: Yup.string().required('Laufzeit ist erforderlich')
      .oneOf(['365', '730'], 'Laufzeit muss entweder 365 oder 730 Tage betragen'),
  }),
  issueDate: Yup.string()
    .matches(/^(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[012])\.\d{4}$/, 'Datum muss im Format TT.MM.JJJJ sein')
    .nullable()
    .transform((value) => (value === '' ? null : value))
});

// Custom Document link component that opens modal directly
const DocumentLink = ({ documentName, displayName, onClick, children }) => {
  return (
    <span
      className="text-primary text-decoration-underline"
      style={{ cursor: 'pointer' }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick(documentName);
      }}
    >
      {children || displayName}
    </span>
  );
};

const GuaranteeForm = () => {
  const { confirmationCode } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!confirmationCode;
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsHandedOver, setTermsHandedOver] = useState(false);
  
  // Document preview state
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);

  // Format a date to ensure it's in DD.MM.YYYY format with leading zeros
  const formatDateWithLeadingZeros = (dateString) => {
    if (!dateString) return '';
    
    try {
      // Handle different date formats - could be YYYY-MM-DD or DD.MM.YYYY
      let day, month, year;
      
      if (dateString.includes('-')) {
        // Format is YYYY-MM-DD
        [year, month, day] = dateString.split('-');
      } else if (dateString.includes('.')) {
        // Format is DD.MM.YYYY
        [day, month, year] = dateString.split('.');
      } else if (dateString.includes(',')) {
        // Format is YYYY,MM,DD
        [year,month,day] = dateString.split(',');
      } else {
        // Unknown format, return as is
        return dateString;
      }
      
      // Ensure leading zeros
      day = day.padStart(2, '0');
      month = month.padStart(2, '0');
      
      return `${day}.${month}.${year}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString; // Return original if there's an error
    }
  };
  
  // Get today's date in DD.MM.YYYY format with leading zeros
  const getTodayFormatted = () => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const [initialValues, setInitialValues] = useState({
    customer: {
      firstName: '',
      lastName: '',
      company: '',
      street: '',
      postalCode: '',
      city: '',
    },
    vehicle: {
      licensePlate: '',
      vin: '',
      duration: '',
    },
    issueDate: getTodayFormatted(), // Properly formatted today's date
  });
  
  const [loading, setLoading] = useState(isEditMode);
  const [error, setError] = useState(null);
  
  // Define document info objects
  const documents = {
    terms: {
      name: 'Mobilitätsgarantie_Martens_Prahl_Mobilitätsgarantiebedingungen.pdf',
      displayName: 'Mobilitätsgarantie Bedingungen',
      url: 'https://select-gruppe.de/fileadmin/Downloads/Mobi/Mobilitätsgarantie_Martens_Prahl_Mobilitätsgarantiebedingungen.pdf',
      type: 'pdf'
    },
    overview: {
      name: '20260101_Übersicht_Mobilitätsgarantie_REKOGA_2026.pdf',
      displayName: 'Übersicht Mobilitätsgarantie REKOGA',
      url: 'https://select-gruppe.de/fileadmin/Downloads/Mobi/20260101_Übersicht_Mobilitätsgarantie_REKOGA_2026.pdf',
      type: 'pdf'
    }
  };
  
  // Document preview function - opens modal directly
  const handleShowPreview = (documentName) => {
    // Find the matching document
    let docToShow = null;
    if (documentName === documents.terms.name) {
      docToShow = documents.terms;
    } else if (documentName === documents.overview.name) {
      docToShow = documents.overview;
    }
    
    if (docToShow) {
      setSelectedDoc(docToShow);
      setShowPreviewModal(true);
    }
  };
  
  const handleDownload = (documentName) => {
    // Find the matching document
    let docUrl = '';
    if (documentName === documents.terms.name) {
      docUrl = documents.terms.url;
    } else if (documentName === documents.overview.name) {
      docUrl = documents.overview.url;
    } else {
      docUrl = `https://select-gruppe.de/fileadmin/Downloads/Mobi/${documentName}`;
    }
    
    // Create and trigger download link
    const link = document.createElement('a');
    link.href = docUrl;
    link.download = documentName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Fetch guarantee data if in edit mode
  useEffect(() => {
    const fetchGuarantee = async () => {
      if (!isEditMode) return;
      
      try {
        setLoading(true);
        const data = await guaranteeService.getById(confirmationCode);
        
        setInitialValues({
          customer: {
            firstName: data.customer.firstName || '',
            lastName: data.customer.lastName || '',
            company: data.customer.company || '',
            street: data.customer.street || '',
            postalCode: data.customer.postalCode || '',
            city: data.customer.city || '',
          },
          vehicle: {
            licensePlate: data.vehicle.licensePlate || '',
            vin: data.vehicle.vin || '',
            duration: data.vehicle.duration || '',
          },
          // Format the date properly
          issueDate: formatDateWithLeadingZeros(data.issueDate),
        });
        setError(null);
      } catch (err) {
        console.error('Error fetching guarantee details:', err);
        setError(err.message || 'Garantiedetails zum Bearbeiten konnten nicht geladen werden. Bitte versuchen Sie es erneut.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchGuarantee();
  }, [confirmationCode, isEditMode]);
  
  const convertToIsoDate = (dateString) => {
    if (!dateString) return null;

    try {
      // Convert from DD.MM.YYYY to ISO date format (YYYY-MM-DD)
      const [day, month, year] = dateString.split('.');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    } catch (error) {
      console.error('Error converting to ISO format:', error);
      return null;
    }
  };
  
  // Handle form submission
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitting(true);
      
      // Convert the date format before sending to API
      const apiValues = {
        ...values,
        issueDate: convertToIsoDate(values.issueDate)
      };
      let result;
      if (isEditMode) {
        result = await guaranteeService.update(confirmationCode, apiValues);
      } else {
        result = await guaranteeService.create(apiValues);
      }
      
      // Check if operation was successful
      if (result.success) {
        // Navigate to the detail view or list with a success message
        if (isEditMode) {
          navigate(`/guarantees/${confirmationCode}`, { 
            state: { message: 'Garantie erfolgreich aktualisiert.' } 
          });
        } else {
          navigate(`/guarantees/${result.data.confirmationCode}`, { 
            state: { message: 'Garantie erfolgreich erstellt.' } 
          });
        }
      } else {
        // Handle error response
        if (result.data && typeof result.data === 'object') {
          // Extract message from response
          let errorMessage = '';
          
          if (result.data.message) {
            errorMessage = result.data.message;
          } else if (result.data.status && typeof result.data.status === 'string' && result.data.status.includes(':')) {
            errorMessage = result.data.status.split(':')[1].trim();
          } else {
            errorMessage = `Garantie konnte nicht ${isEditMode ? 'aktualisiert' : 'erstellt'} werden. Bitte überprüfen Sie Ihre Eingaben und versuchen Sie es erneut.`;
          }
          
          setError(errorMessage);
        } else {
          // Use the message from the result
          setError(result.message || `Garantie konnte nicht ${isEditMode ? 'aktualisiert' : 'erstellt'} werden. Bitte überprüfen Sie Ihre Eingaben und versuchen Sie es erneut.`);
        }
      }
    } catch (err) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} guarantee:`, err);
      setError(err.message || `Fehler beim ${isEditMode ? 'Aktualisieren' : 'Erstellen'} der Garantie: Unbekannter Fehler`);
    } finally {
      setSubmitting(false);
    }
  };
  
  // Handle date input change to ensure proper format
  const handleDateChange = (e, handleChange) => {
    // Allow the Formik handleChange to process the event first
    handleChange(e);
    
    const value = e.target.value;
    if (!value) return;
    
    // Get the input element
    const input = e.target;
    
    // If the user is typing, help format the date
    let formatted = value.replace(/[^\d.]/g, ''); // Remove anything that's not a digit or period
    
    // If we have at least some content, try to format it
    if (formatted.length > 0) {
      // Split by periods
      const parts = formatted.split('.');
      
      // Format day (first part)
      if (parts[0] && parts[0].length > 0) {
        let day = parts[0];
        // If day is greater than 31, cap it
        if (parseInt(day) > 31) day = '31';
        // Add leading zero if needed and it's not already being entered
        if (day.length === 1 && formatted.length > 2) day = day.padStart(2, '0');
        parts[0] = day;
      }
      
      // Format month (second part) if we have it
      if (parts[1] && parts[1].length > 0) {
        let month = parts[1];
        // If month is greater than 12, cap it
        if (parseInt(month) > 12) month = '12';
        // Add leading zero if needed and it's not already being entered
        if (month.length === 1 && formatted.length > 5) month = month.padStart(2, '0');
        parts[1] = month;
      }
      
      // Reconstruct the formatted date
      formatted = parts.join('.');
    }
    
    // Update the input value if it's different
    if (formatted !== value) {
      // Manually update the input's value
      input.value = formatted;
      
      // Create a synthetic event to update Formik's state
      const syntheticEvent = {
        target: {
          name: input.name,
          value: formatted
        }
      };
      handleChange(syntheticEvent);
    }
  };
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  return (
    <Container>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>{isEditMode ? 'Mobilitätsgarantie bearbeiten' : 'Mobilitätsgarantie erstellen'}</h2>
        <Button 
          variant="outline-primary" 
          as={Link} 
          to={isEditMode ? `/guarantees/${confirmationCode}` : '/guarantees'}
        >
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          {isEditMode ? 'Zurück zu den Details' : 'Zurück zur Liste'}
        </Button>
      </div>
      
      {error && (
        <ErrorMessage message={error} onDismiss={() => setError(null)} />
      )}
      
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
        }) => (
          <Form onSubmit={handleSubmit}>
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">Kundeninformationen</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Vorname *</Form.Label>
                      <Form.Control
                        type="text"
                        name="customer.firstName"
                        value={values.customer.firstName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.customer?.firstName && errors.customer?.firstName}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.customer?.firstName}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Nachname *</Form.Label>
                      <Form.Control
                        type="text"
                        name="customer.lastName"
                        value={values.customer.lastName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.customer?.lastName && errors.customer?.lastName}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.customer?.lastName}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
                
                <Form.Group className="mb-3">
                  <Form.Label>Firma</Form.Label>
                  <Form.Control
                    type="text"
                    name="customer.company"
                    value={values.customer.company}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Straße *</Form.Label>
                  <Form.Control
                    type="text"
                    name="customer.street"
                    value={values.customer.street}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.customer?.street && errors.customer?.street}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.customer?.street}
                  </Form.Control.Feedback>
                </Form.Group>
                
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Postleitzahl *</Form.Label>
                      <Form.Control
                        type="text"
                        name="customer.postalCode"
                        value={values.customer.postalCode}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.customer?.postalCode && errors.customer?.postalCode}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.customer?.postalCode}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={8}>
                    <Form.Group className="mb-3">
                      <Form.Label>Stadt *</Form.Label>
                      <Form.Control
                        type="text"
                        name="customer.city"
                        value={values.customer.city}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.customer?.city && errors.customer?.city}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.customer?.city}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
            
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">Fahrzeuginformationen</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Kennzeichen *</Form.Label>
                      <Form.Control
                        type="text"
                        name="vehicle.licensePlate"
                        value={values.vehicle.licensePlate}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.vehicle?.licensePlate && errors.vehicle?.licensePlate}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.vehicle?.licensePlate}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>VIN (Optional)</Form.Label>
                      <Form.Control
                        type="text"
                        name="vehicle.vin"
                        value={values.vehicle.vin}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Laufzeit *</Form.Label>
                      <Form.Select
                        name="vehicle.duration"
                        value={values.vehicle.duration}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.vehicle?.duration && errors.vehicle?.duration}
                        disabled={isEditMode} // Disable in edit mode
                      >
                        <option value="">Laufzeit auswählen...</option>
                        <option value="365">365 Tage (1 Jahr)</option>
                        <option value="730">730 Tage (2 Jahre)</option>
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.vehicle?.duration}
                      </Form.Control.Feedback>
                      {isEditMode && (
                        <Form.Text className="text-muted">
                          Die Laufzeit kann im Bearbeitungsmodus nicht geändert werden.
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Ausstellungsdatum (TT.MM.JJJJ)</Form.Label>
                      <Form.Control
                        type="text"
                        name="issueDate"
                        placeholder="TT.MM.JJJJ"
                        value={values.issueDate}
                        onChange={(e) => handleDateChange(e, handleChange)}
                        onBlur={handleBlur}
                        isInvalid={touched.issueDate && errors.issueDate}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.issueDate}
                      </Form.Control.Feedback>
                      <Form.Text className="text-muted">
                        Falls nicht angegeben, wird das heutige Datum verwendet.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Only show price and terms in create mode, not in edit mode */}
                {!isEditMode && (
                    <Card className="mb-4">
                      <Card.Header>
                        <h5 className="mb-0">Bedingungen und Preise</h5>
                      </Card.Header>
                      <Card.Body>
                        <div className="mb-3">
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <GuaranteePrice duration={values.vehicle.duration || 365} />
                          </div>
                          
                          <Form.Group className="mb-3">
                            <div className="d-flex align-items-start">
                              <Form.Check 
                                type="checkbox"
                                id="termsAccepted"
                                label={
                                  <span>
                                    Ich habe die <DocumentLink 
                                      documentName={documents.overview.name}
                                      displayName={documents.overview.displayName}
                                      onClick={handleShowPreview}
                                    >
                                      Übersicht Mobilitätsgarantie REKOGA
                                    </DocumentLink> gelesen
                                  </span>
                                }
                                checked={termsAccepted}
                                onChange={e => setTermsAccepted(e.target.checked)}
                                required
                              />
                            </div>
                          </Form.Group>
                          
                          <Form.Group className="mb-3">
                            <div className="d-flex align-items-start">
                              <Form.Check 
                                type="checkbox"
                                id="termsHandedOver"
                                label={
                                  <span>
                                    Ich habe die <DocumentLink 
                                      documentName={documents.terms.name}
                                      displayName={documents.terms.displayName}
                                      onClick={handleShowPreview}
                                    >
                                      Mobilitätsgarantie Bedingungen
                                    </DocumentLink> an meinen Kunden ausgehändigt
                                  </span>
                                }
                                checked={termsHandedOver}
                                onChange={e => setTermsHandedOver(e.target.checked)}
                                required
                              />
                            </div>
                          </Form.Group>
                        </div>
                      </Card.Body>
                    </Card>
                )}
              </Card.Body>
            </Card>
            

            <div className="d-flex justify-content-end">
              <Button
                variant="secondary"
                as={Link}
                to={isEditMode ? `/guarantees/${confirmationCode}` : '/guarantees'}
                className="me-2"
              >
                Abbrechen
              </Button>

              <Button 
                type="submit" 
                variant="primary" 
                disabled={isSubmitting || (!isEditMode && (!termsAccepted || !termsHandedOver))}
              >
                <FontAwesomeIcon icon={faSave} className="me-2" />
                {isSubmitting ? 'Wird gespeichert...' : 'Garantie speichern'}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
      
      {/* Document Preview Modal */}
      <Modal 
        show={showPreviewModal} 
        onHide={() => setShowPreviewModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon 
              icon={faFilePdf} 
              className="me-2" 
              color="#dc3545" 
            />
            {selectedDoc?.displayName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <div className="mb-3 small text-muted">
              {selectedDoc?.name}
            </div>
            <iframe 
              src={selectedDoc?.url}
              width="100%" 
              height="500px" 
              title={selectedDoc?.displayName}
              className="border-0"
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>
            Schließen
          </Button>
          <Button 
            variant="primary" 
            onClick={() => selectedDoc && handleDownload(selectedDoc.name)}
          >
            <FontAwesomeIcon icon={faDownload} className="me-1" />
            Herunterladen
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default GuaranteeForm;